import * as React from 'react';

/**
 * Fired by the app's browser-back handling (`DialogBrowserBack`) at the
 * overlay of the topmost `Modal`, without bubbling, so browser back closes
 * that modal alone. Escape cannot do this: `Modal` hears it on `window`, so
 * one Escape closes every open modal at once.
 */
export const MODAL_BACK_CLOSE_EVENT = 'mfw:modal-back-close';

/**
 * A callback ref for a modal's overlay that runs `onBackClose` when browser
 * back targets that overlay. Stable, so it can be called from an inline ref
 * on every render: re-adding the same listener to the same node is a no-op.
 */
export function useModalBackCloseRef(onBackClose: () => void) {
  const onBackCloseRef = React.useRef(onBackClose);
  onBackCloseRef.current = onBackClose;

  return React.useMemo(() => {
    let overlay: HTMLElement | null = null;
    const listener = () => onBackCloseRef.current();
    return (element: HTMLElement | null) => {
      overlay?.removeEventListener(MODAL_BACK_CLOSE_EVENT, listener);
      element?.addEventListener(MODAL_BACK_CLOSE_EVENT, listener);
      overlay = element;
    };
  }, []);
}
